/**
 * Swiss Standard Kontenplan Templates
 * KMU = Kontenrahmen für kleine und mittlere Unternehmen (SME Chart of Accounts)
 * Käfer = Käfer Kontenrahmen (Alternative Swiss standard)
 */

export interface KontenplanAccount {
  code: string;
  name: string;
  nameGerman: string;
  nameFrench?: string;
  nameItalian?: string;
  type: "asset" | "liability" | "equity" | "revenue" | "expense";
  category: string;
  subcategory?: string;
  parentCode?: string;
  level: number;
  vatApplicable: boolean;
  vatRate?: number; // basis points
}

/**
 * KMU Kontenrahmen - Swiss SME Standard
 * Based on Swiss GAAP FER and common SME practices
 */
export const KMU_TEMPLATE: KontenplanAccount[] = [
  // 1xxx - ASSETS (Aktiven)
  {
    code: "1000",
    name: "Assets",
    nameGerman: "Aktiven",
    type: "asset",
    category: "Assets",
    level: 1,
    vatApplicable: false,
  },

  // 10xx - Current Assets (Umlaufvermögen)
  {
    code: "1000",
    name: "Current Assets",
    nameGerman: "Umlaufvermögen",
    type: "asset",
    category: "Current Assets",
    level: 2,
    vatApplicable: false,
  },
  {
    code: "1020",
    name: "Cash",
    nameGerman: "Kasse",
    type: "asset",
    category: "Current Assets",
    subcategory: "Cash",
    parentCode: "1000",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "1021",
    name: "Petty Cash",
    nameGerman: "Portokasse",
    type: "asset",
    category: "Current Assets",
    subcategory: "Cash",
    parentCode: "1020",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "1030",
    name: "Bank Accounts",
    nameGerman: "Bankkonten",
    type: "asset",
    category: "Current Assets",
    subcategory: "Bank",
    parentCode: "1000",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "1031",
    name: "PostFinance",
    nameGerman: "PostFinance",
    type: "asset",
    category: "Current Assets",
    subcategory: "Bank",
    parentCode: "1030",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "1032",
    name: "UBS",
    nameGerman: "UBS",
    type: "asset",
    category: "Current Assets",
    subcategory: "Bank",
    parentCode: "1030",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "1033",
    name: "Credit Suisse",
    nameGerman: "Credit Suisse",
    type: "asset",
    category: "Current Assets",
    subcategory: "Bank",
    parentCode: "1030",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "1100",
    name: "Accounts Receivable",
    nameGerman: "Forderungen aus Lieferungen und Leistungen",
    type: "asset",
    category: "Current Assets",
    subcategory: "Receivables",
    parentCode: "1000",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "1101",
    name: "Trade Receivables",
    nameGerman: "Debitoren",
    type: "asset",
    category: "Current Assets",
    subcategory: "Receivables",
    parentCode: "1100",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "1109",
    name: "Allowance for Doubtful Accounts",
    nameGerman: "Delkredere",
    type: "asset",
    category: "Current Assets",
    subcategory: "Receivables",
    parentCode: "1100",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "1170",
    name: "VAT Receivable",
    nameGerman: "Vorsteuer",
    type: "asset",
    category: "Current Assets",
    subcategory: "VAT",
    parentCode: "1000",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "1171",
    name: "Input VAT 7.7%",
    nameGerman: "Vorsteuer 7.7%",
    type: "asset",
    category: "Current Assets",
    subcategory: "VAT",
    parentCode: "1170",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "1172",
    name: "Input VAT 2.5%",
    nameGerman: "Vorsteuer 2.5%",
    type: "asset",
    category: "Current Assets",
    subcategory: "VAT",
    parentCode: "1170",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "1200",
    name: "Inventory",
    nameGerman: "Vorräte",
    type: "asset",
    category: "Current Assets",
    subcategory: "Inventory",
    parentCode: "1000",
    level: 3,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "1210",
    name: "Raw Materials",
    nameGerman: "Rohmaterial",
    type: "asset",
    category: "Current Assets",
    subcategory: "Inventory",
    parentCode: "1200",
    level: 4,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "1220",
    name: "Work in Progress",
    nameGerman: "Halbfabrikate",
    type: "asset",
    category: "Current Assets",
    subcategory: "Inventory",
    parentCode: "1200",
    level: 4,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "1230",
    name: "Finished Goods",
    nameGerman: "Fertigfabrikate",
    type: "asset",
    category: "Current Assets",
    subcategory: "Inventory",
    parentCode: "1200",
    level: 4,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "1260",
    name: "Prepaid Expenses",
    nameGerman: "Aktive Rechnungsabgrenzung",
    type: "asset",
    category: "Current Assets",
    subcategory: "Prepaid",
    parentCode: "1000",
    level: 3,
    vatApplicable: false,
  },

  // 14xx - Fixed Assets (Anlagevermögen)
  {
    code: "1400",
    name: "Fixed Assets",
    nameGerman: "Anlagevermögen",
    type: "asset",
    category: "Fixed Assets",
    level: 2,
    vatApplicable: false,
  },
  {
    code: "1440",
    name: "Tangible Assets",
    nameGerman: "Sachanlagen",
    type: "asset",
    category: "Fixed Assets",
    subcategory: "Tangible",
    parentCode: "1400",
    level: 3,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "1441",
    name: "Machinery & Equipment",
    nameGerman: "Maschinen und Apparate",
    type: "asset",
    category: "Fixed Assets",
    subcategory: "Tangible",
    parentCode: "1440",
    level: 4,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "1442",
    name: "Furniture & Fixtures",
    nameGerman: "Mobiliar und Einrichtungen",
    type: "asset",
    category: "Fixed Assets",
    subcategory: "Tangible",
    parentCode: "1440",
    level: 4,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "1443",
    name: "Vehicles",
    nameGerman: "Fahrzeuge",
    type: "asset",
    category: "Fixed Assets",
    subcategory: "Tangible",
    parentCode: "1440",
    level: 4,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "1444",
    name: "IT Equipment",
    nameGerman: "Informatik",
    type: "asset",
    category: "Fixed Assets",
    subcategory: "Tangible",
    parentCode: "1440",
    level: 4,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "1480",
    name: "Intangible Assets",
    nameGerman: "Immaterielle Anlagen",
    type: "asset",
    category: "Fixed Assets",
    subcategory: "Intangible",
    parentCode: "1400",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "1481",
    name: "Software",
    nameGerman: "Software",
    type: "asset",
    category: "Fixed Assets",
    subcategory: "Intangible",
    parentCode: "1480",
    level: 4,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "1482",
    name: "Licenses",
    nameGerman: "Lizenzen",
    type: "asset",
    category: "Fixed Assets",
    subcategory: "Intangible",
    parentCode: "1480",
    level: 4,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "1490",
    name: "Financial Assets",
    nameGerman: "Finanzanlagen",
    type: "asset",
    category: "Fixed Assets",
    subcategory: "Financial",
    parentCode: "1400",
    level: 3,
    vatApplicable: false,
  },

  // 2xxx - LIABILITIES (Fremdkapital)
  {
    code: "2000",
    name: "Liabilities",
    nameGerman: "Fremdkapital",
    type: "liability",
    category: "Liabilities",
    level: 1,
    vatApplicable: false,
  },

  // 20xx - Current Liabilities (Kurzfristiges Fremdkapital)
  {
    code: "2000",
    name: "Current Liabilities",
    nameGerman: "Kurzfristiges Fremdkapital",
    type: "liability",
    category: "Current Liabilities",
    level: 2,
    vatApplicable: false,
  },
  {
    code: "2030",
    name: "Accounts Payable",
    nameGerman: "Verbindlichkeiten aus Lieferungen und Leistungen",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "Payables",
    parentCode: "2000",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "2031",
    name: "Trade Payables",
    nameGerman: "Kreditoren",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "Payables",
    parentCode: "2030",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "2100",
    name: "Short-term Debt",
    nameGerman: "Kurzfristige Bankverbindlichkeiten",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "Debt",
    parentCode: "2000",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "2200",
    name: "VAT Payable",
    nameGerman: "Mehrwertsteuer",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "VAT",
    parentCode: "2000",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "2201",
    name: "Output VAT 7.7%",
    nameGerman: "Umsatzsteuer 7.7%",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "VAT",
    parentCode: "2200",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "2202",
    name: "Output VAT 2.5%",
    nameGerman: "Umsatzsteuer 2.5%",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "VAT",
    parentCode: "2200",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "2203",
    name: "Output VAT 3.7%",
    nameGerman: "Umsatzsteuer 3.7%",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "VAT",
    parentCode: "2200",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "2206",
    name: "VAT Payable to Tax Authority",
    nameGerman: "Geschuldete MWST",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "VAT",
    parentCode: "2200",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "2300",
    name: "Other Current Liabilities",
    nameGerman: "Übrige kurzfristige Verbindlichkeiten",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "Other",
    parentCode: "2000",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "2330",
    name: "Accrued Expenses",
    nameGerman: "Passive Rechnungsabgrenzung",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "Accruals",
    parentCode: "2000",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "2400",
    name: "Social Security Payable",
    nameGerman: "Sozialversicherungen",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "Social",
    parentCode: "2000",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "2410",
    name: "AHV/IV/EO Payable",
    nameGerman: "AHV/IV/EO",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "Social",
    parentCode: "2400",
    level: 4,
    vatApplicable: false,
  },
  {
    code: "2420",
    name: "Pension Fund Payable",
    nameGerman: "Pensionskasse",
    type: "liability",
    category: "Current Liabilities",
    subcategory: "Social",
    parentCode: "2400",
    level: 4,
    vatApplicable: false,
  },

  // 24xx - Long-term Liabilities (Langfristiges Fremdkapital)
  {
    code: "2400",
    name: "Long-term Liabilities",
    nameGerman: "Langfristiges Fremdkapital",
    type: "liability",
    category: "Long-term Liabilities",
    level: 2,
    vatApplicable: false,
  },
  {
    code: "2500",
    name: "Long-term Debt",
    nameGerman: "Langfristige Bankverbindlichkeiten",
    type: "liability",
    category: "Long-term Liabilities",
    subcategory: "Debt",
    parentCode: "2400",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "2600",
    name: "Provisions",
    nameGerman: "Rückstellungen",
    type: "liability",
    category: "Long-term Liabilities",
    subcategory: "Provisions",
    parentCode: "2400",
    level: 3,
    vatApplicable: false,
  },

  // 2xxx - EQUITY (Eigenkapital)
  {
    code: "2800",
    name: "Equity",
    nameGerman: "Eigenkapital",
    type: "equity",
    category: "Equity",
    level: 1,
    vatApplicable: false,
  },
  {
    code: "2800",
    name: "Share Capital",
    nameGerman: "Grundkapital",
    type: "equity",
    category: "Equity",
    subcategory: "Capital",
    level: 2,
    vatApplicable: false,
  },
  {
    code: "2900",
    name: "Reserves",
    nameGerman: "Reserven",
    type: "equity",
    category: "Equity",
    subcategory: "Reserves",
    level: 2,
    vatApplicable: false,
  },
  {
    code: "2950",
    name: "Retained Earnings",
    nameGerman: "Gewinnvortrag",
    type: "equity",
    category: "Equity",
    subcategory: "Retained",
    level: 2,
    vatApplicable: false,
  },
  {
    code: "2979",
    name: "Current Year Profit/Loss",
    nameGerman: "Jahresgewinn/-verlust",
    type: "equity",
    category: "Equity",
    subcategory: "Current",
    level: 2,
    vatApplicable: false,
  },

  // 3xxx - REVENUE (Ertrag)
  {
    code: "3000",
    name: "Revenue",
    nameGerman: "Ertrag",
    type: "revenue",
    category: "Revenue",
    level: 1,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "3200",
    name: "Sales Revenue",
    nameGerman: "Warenertrag",
    type: "revenue",
    category: "Revenue",
    subcategory: "Sales",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "3400",
    name: "Service Revenue",
    nameGerman: "Dienstleistungsertrag",
    type: "revenue",
    category: "Revenue",
    subcategory: "Services",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "3600",
    name: "Other Operating Revenue",
    nameGerman: "Übrige Erträge",
    type: "revenue",
    category: "Revenue",
    subcategory: "Other",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "3800",
    name: "Financial Income",
    nameGerman: "Finanzertrag",
    type: "revenue",
    category: "Revenue",
    subcategory: "Financial",
    level: 2,
    vatApplicable: false,
  },

  // 4xxx - EXPENSES (Materialaufwand)
  {
    code: "4000",
    name: "Material Expenses",
    nameGerman: "Materialaufwand",
    type: "expense",
    category: "Material Expenses",
    level: 1,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "4200",
    name: "Purchases of Goods",
    nameGerman: "Wareneinkauf",
    type: "expense",
    category: "Material Expenses",
    subcategory: "Goods",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "4400",
    name: "Purchases of Materials",
    nameGerman: "Materialeinkauf",
    type: "expense",
    category: "Material Expenses",
    subcategory: "Materials",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "4900",
    name: "Inventory Changes",
    nameGerman: "Bestandesänderungen",
    type: "expense",
    category: "Material Expenses",
    subcategory: "Inventory",
    level: 2,
    vatApplicable: false,
  },

  // 5xxx - PERSONNEL EXPENSES (Personalaufwand)
  {
    code: "5000",
    name: "Personnel Expenses",
    nameGerman: "Personalaufwand",
    type: "expense",
    category: "Personnel Expenses",
    level: 1,
    vatApplicable: false,
  },
  {
    code: "5200",
    name: "Salaries & Wages",
    nameGerman: "Löhne und Gehälter",
    type: "expense",
    category: "Personnel Expenses",
    subcategory: "Salaries",
    level: 2,
    vatApplicable: false,
  },
  {
    code: "5700",
    name: "Social Security Expenses",
    nameGerman: "Sozialversicherungsaufwand",
    type: "expense",
    category: "Personnel Expenses",
    subcategory: "Social",
    level: 2,
    vatApplicable: false,
  },
  {
    code: "5710",
    name: "AHV/IV/EO Employer",
    nameGerman: "AHV/IV/EO Arbeitgeber",
    type: "expense",
    category: "Personnel Expenses",
    subcategory: "Social",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "5720",
    name: "Pension Fund Employer",
    nameGerman: "Pensionskasse Arbeitgeber",
    type: "expense",
    category: "Personnel Expenses",
    subcategory: "Social",
    level: 3,
    vatApplicable: false,
  },
  {
    code: "5800",
    name: "Other Personnel Expenses",
    nameGerman: "Übriger Personalaufwand",
    type: "expense",
    category: "Personnel Expenses",
    subcategory: "Other",
    level: 2,
    vatApplicable: false,
  },

  // 6xxx - OPERATING EXPENSES (Betriebsaufwand)
  {
    code: "6000",
    name: "Operating Expenses",
    nameGerman: "Betriebsaufwand",
    type: "expense",
    category: "Operating Expenses",
    level: 1,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "6100",
    name: "Rent Expenses",
    nameGerman: "Raumaufwand",
    type: "expense",
    category: "Operating Expenses",
    subcategory: "Rent",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "6200",
    name: "Maintenance & Repairs",
    nameGerman: "Unterhalt und Reparaturen",
    type: "expense",
    category: "Operating Expenses",
    subcategory: "Maintenance",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "6300",
    name: "Vehicle Expenses",
    nameGerman: "Fahrzeugaufwand",
    type: "expense",
    category: "Operating Expenses",
    subcategory: "Vehicles",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "6400",
    name: "Insurance Expenses",
    nameGerman: "Versicherungsaufwand",
    type: "expense",
    category: "Operating Expenses",
    subcategory: "Insurance",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "6500",
    name: "Energy & Utilities",
    nameGerman: "Energie- und Entsorgungsaufwand",
    type: "expense",
    category: "Operating Expenses",
    subcategory: "Utilities",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "6600",
    name: "Administrative Expenses",
    nameGerman: "Verwaltungsaufwand",
    type: "expense",
    category: "Operating Expenses",
    subcategory: "Admin",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "6700",
    name: "IT & Communication",
    nameGerman: "Informatik- und Kommunikationsaufwand",
    type: "expense",
    category: "Operating Expenses",
    subcategory: "IT",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "6800",
    name: "Advertising & Marketing",
    nameGerman: "Werbeaufwand",
    type: "expense",
    category: "Operating Expenses",
    subcategory: "Marketing",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "6900",
    name: "Other Operating Expenses",
    nameGerman: "Übriger Betriebsaufwand",
    type: "expense",
    category: "Operating Expenses",
    subcategory: "Other",
    level: 2,
    vatApplicable: true,
    vatRate: 770,
  },

  // 6xxx - DEPRECIATION (Abschreibungen)
  {
    code: "6900",
    name: "Depreciation & Amortization",
    nameGerman: "Abschreibungen",
    type: "expense",
    category: "Depreciation",
    level: 1,
    vatApplicable: false,
  },
  {
    code: "6940",
    name: "Depreciation on Tangible Assets",
    nameGerman: "Abschreibungen Sachanlagen",
    type: "expense",
    category: "Depreciation",
    subcategory: "Tangible",
    level: 2,
    vatApplicable: false,
  },
  {
    code: "6950",
    name: "Amortization on Intangible Assets",
    nameGerman: "Abschreibungen immaterielle Anlagen",
    type: "expense",
    category: "Depreciation",
    subcategory: "Intangible",
    level: 2,
    vatApplicable: false,
  },

  // 6xxx - FINANCIAL EXPENSES (Finanzaufwand)
  {
    code: "6950",
    name: "Financial Expenses",
    nameGerman: "Finanzaufwand",
    type: "expense",
    category: "Financial Expenses",
    level: 1,
    vatApplicable: false,
  },
  {
    code: "6960",
    name: "Interest Expenses",
    nameGerman: "Zinsaufwand",
    type: "expense",
    category: "Financial Expenses",
    subcategory: "Interest",
    level: 2,
    vatApplicable: false,
  },
  {
    code: "6970",
    name: "Bank Charges",
    nameGerman: "Bankspesen",
    type: "expense",
    category: "Financial Expenses",
    subcategory: "Fees",
    level: 2,
    vatApplicable: false,
  },
];

/**
 * Käfer Kontenrahmen - Alternative Swiss Standard
 * Simplified version for very small businesses
 */
export const KAEFER_TEMPLATE: KontenplanAccount[] = [
  // Simplified structure - only essential accounts
  {
    code: "1000",
    name: "Cash",
    nameGerman: "Kasse",
    type: "asset",
    category: "Current Assets",
    level: 1,
    vatApplicable: false,
  },
  {
    code: "1020",
    name: "Bank",
    nameGerman: "Bank",
    type: "asset",
    category: "Current Assets",
    level: 1,
    vatApplicable: false,
  },
  {
    code: "1100",
    name: "Receivables",
    nameGerman: "Debitoren",
    type: "asset",
    category: "Current Assets",
    level: 1,
    vatApplicable: false,
  },
  {
    code: "1170",
    name: "Input VAT",
    nameGerman: "Vorsteuer",
    type: "asset",
    category: "Current Assets",
    level: 1,
    vatApplicable: false,
  },
  {
    code: "1400",
    name: "Fixed Assets",
    nameGerman: "Anlagevermögen",
    type: "asset",
    category: "Fixed Assets",
    level: 1,
    vatApplicable: false,
  },

  {
    code: "2000",
    name: "Payables",
    nameGerman: "Kreditoren",
    type: "liability",
    category: "Current Liabilities",
    level: 1,
    vatApplicable: false,
  },
  {
    code: "2200",
    name: "Output VAT",
    nameGerman: "Umsatzsteuer",
    type: "liability",
    category: "Current Liabilities",
    level: 1,
    vatApplicable: false,
  },
  {
    code: "2800",
    name: "Equity",
    nameGerman: "Eigenkapital",
    type: "equity",
    category: "Equity",
    level: 1,
    vatApplicable: false,
  },

  {
    code: "3000",
    name: "Revenue",
    nameGerman: "Ertrag",
    type: "revenue",
    category: "Revenue",
    level: 1,
    vatApplicable: true,
    vatRate: 770,
  },

  {
    code: "4000",
    name: "Material Expenses",
    nameGerman: "Materialaufwand",
    type: "expense",
    category: "Expenses",
    level: 1,
    vatApplicable: true,
    vatRate: 770,
  },
  {
    code: "5000",
    name: "Personnel Expenses",
    nameGerman: "Personalaufwand",
    type: "expense",
    category: "Expenses",
    level: 1,
    vatApplicable: false,
  },
  {
    code: "6000",
    name: "Operating Expenses",
    nameGerman: "Betriebsaufwand",
    type: "expense",
    category: "Expenses",
    level: 1,
    vatApplicable: true,
    vatRate: 770,
  },
];

export function getKontenplanTemplate(
  template: "kmu" | "kaefer" | "custom"
): KontenplanAccount[] {
  switch (template) {
    case "kmu":
      return KMU_TEMPLATE;
    case "kaefer":
      return KAEFER_TEMPLATE;
    case "custom":
      return [];
    default:
      return KMU_TEMPLATE;
  }
}
